// 플랫폼별 포맷 어휘 — 슬롯(long/short)은 BE 계약이라 그대로 두고 **화면 어휘만** 갈린다.
//
// TikTok 은 전부 영상이라 칸이 하나다. 없는 구분을 억지로 그리면 화면이 "LONG —"처럼
// 존재하지 않는 포맷을 주장하고, 그 빈칸의 결측 사유("집계 콘텐츠 없음")까지 따라 붙어
// 값이 멀쩡한 줄에 모순된 문구가 남는다.
//
// 그래서 **칸 · 결측 사유 · 모수** 세 문구가 전부 이 표 하나에서 갈린다. 한 곳만 고치면
// 같은 화면의 다른 블록이 다른 말을 하게 된다(Overview 채널 퍼포먼스 vs Performance KPI).
//
// label = 칸에 찍히는 표기(디자인 확정, YouTube 는 대문자) / prose = 문장 안에서 쓰는 표기.
// 둘을 나눈 이유는 "LONG·SHORT 모두 집계 콘텐츠 없음" 같은 한국어 문장을 만들지 않기 위해서다.
import type { ContentFormat, FormatPair, Platform } from '../creatorsApi'
import { isMissing, type MetricStatus } from './metric'

export interface FormatSlot {
  slot: ContentFormat
  /** 칸 라벨 */
  label: string
  /** 문장 안 표기 */
  prose: string
}

export const FORMAT_LABELS: Record<Platform, FormatSlot[]> = {
  youtube: [
    { slot: 'long', label: 'LONG', prose: '롱' },
    { slot: 'short', label: 'SHORT', prose: '숏' },
  ],
  instagram: [
    { slot: 'long', label: '게시물', prose: '게시물' },
    { slot: 'short', label: '릴스', prose: '릴스' },
  ],
  tiktok: [{ slot: 'short', label: '영상', prose: '영상' }],
}

/** 그 플랫폼이 실제로 그리는 슬롯. 화이트리스트 밖 social_type 이 흘러와도 화면이 비지 않게 폴백. */
export function formatSlots(platform: Platform): FormatSlot[] {
  return FORMAT_LABELS[platform] ?? FORMAT_LABELS.youtube
}

/**
 * 결측 사유 1줄 — **그리는 칸만**, 그 칸의 어휘로.
 *
 * 칸이 하나면(TikTok) 접두어를 붙이지 않는다. 밝힐 다른 칸이 없는데 "영상 집계 콘텐츠 없음"이라
 * 쓰면 동어반복이다. 칸이 둘이면 어느 쪽인지 밝혀야 사용자가 볼 곳을 안다.
 * 결측이 없으면 null — 호출부는 줄 자체를 그리지 않는다.
 */
export function missingCaption(
  platform: Platform,
  byslot: Record<ContentFormat, MetricStatus>,
): string | null {
  const slots = formatSlots(platform)
  const missing = slots
    .map((f) => ({ prose: f.prose, caption: isMissing(byslot[f.slot].state) ? byslot[f.slot].caption : null }))
    .filter((s): s is { prose: string; caption: string } => Boolean(s.caption))
  if (missing.length === 0) return null
  if (missing.length === 1) {
    const only = missing[0]
    return slots.length === 1 ? only.caption : `${only.prose} ${only.caption}`
  }
  const [a, b] = missing
  return a.caption === b.caption
    ? `${a.prose}·${b.prose} 모두 ${a.caption}`
    : `${a.prose} ${a.caption} · ${b.prose} ${b.caption}`
}

/**
 * 모수 표기 — 그리는 칸만 센다. "롱 42 · 숏 18건" / TikTok "23건".
 * 미도달(전부 null)이면 null, 전부 0이면 "0건"(0건도 사실이므로 숨기지 않는다).
 */
export function sampleText(platform: Platform, sample?: FormatPair | null): string | null {
  if (!sample) return null
  const slots = formatSlots(platform)
  const counts = slots.map((f) => ({ prose: f.prose, n: sample[f.slot] }))
  if (counts.every((c) => c.n == null)) return null
  if (slots.length === 1) return `${(counts[0].n ?? 0).toLocaleString('ko-KR')}건`
  if (counts.every((c) => (c.n ?? 0) === 0)) return '0건'
  return `${counts.map((c) => `${c.prose} ${(c.n ?? 0).toLocaleString('ko-KR')}`).join(' · ')}건`
}

/** 값 쌍 병기(중앙값 등) — 그리는 칸만. "롱 9.8만 · 숏 31.2만" / TikTok "31.2만". */
export function valuePairText(
  platform: Platform,
  pair: FormatPair | undefined,
  fmt: (v: number | null) => string,
): string | null {
  if (!pair) return null
  const slots = formatSlots(platform)
  const parts = slots
    .filter((f) => pair[f.slot] != null)
    .map((f) => (slots.length === 1 ? fmt(pair[f.slot]) : `${f.prose} ${fmt(pair[f.slot])}`))
  return parts.length ? parts.join(' · ') : null
}
